import React, { useCallback, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { Icon } from '@iconify/react'
import { track } from '@amplitude/analytics-browser'
import { PopoverPanel, Toggle, colors } from 'src/components/app-ui'
import useCatalogListing from 'src/hooks/useCatalogListing'
import { useCopy } from './use-course-links'
import useStyles from './styles'

const LINK_CARDS = [
  {
    key: 'portal',
    title: 'Portal link',
    badge: 'IN PORTAL',
    badgeStyle: { color: colors.primary, background: colors.primarySoft },
    hint:
      'Opens the course inside your portal, with its header, navigation and catalog. Use it for learners who already use the portal.',
  },
  {
    key: 'direct',
    title: 'Direct link',
    badge: 'STANDALONE',
    badgeStyle: { color: colors.slate, background: colors.chip },
    hint:
      'Only this course, with no portal navigation. Use it in emails and campaigns. Embed, iFrame and SCORM use this link too.',
  },
]

/** The domain the direct link is on. */
const DomainPicker = ({ links }) => {
  const classes = useStyles()
  const history = useHistory()
  const [open, setOpen] = useState(false)
  const close = useCallback(() => setOpen(false), [])

  return (
    <PopoverPanel
      open={open}
      onClose={close}
      width={280}
      align="right"
      panelStyle={{ top: '100%', marginTop: 4, borderRadius: 8 }}
      trigger={
        <button
          type="button"
          title="Domain"
          className={classes.domainButton}
          onClick={() => setOpen(value => !value)}
        >
          <Icon icon="tabler:world" width={13} height={13} />
          {links.domain}
          <Icon
            icon="tabler:chevron-down"
            width={12}
            height={12}
            color={colors.muted}
          />
        </button>
      }
    >
      <div className={classes.domainList}>
        {links.domains.map(item => {
          const on = item.label === links.domain
          return (
            <button
              key={item.label}
              type="button"
              className={classes.domainOption}
              style={{
                background: on ? 'rgba(1,95,251,.08)' : 'transparent',
                color: on ? colors.primary : colors.navy,
              }}
              onClick={() => {
                links.setDomain(item.label)
                close()
              }}
            >
              {item.label}
              <span className={classes.domainKind}>{item.kind}</span>
            </button>
          )
        })}
        <button
          type="button"
          className={classes.domainSetup}
          onClick={() => history.push('/workspace/custom-domain')}
        >
          Set up a custom domain
        </button>
      </div>
    </PopoverPanel>
  )
}

/** Share › Links: the portal and direct links, catalog listing and status. */
const LinksView = ({ course, links, publishState, onMoveToDraft }) => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const [copied, copy] = useCopy()
  const [publishing, setPublishing] = useState(false)
  const [listedInCatalog, setListedInCatalog] = useCatalogListing(
    'course',
    course,
  )
  const isPublished = Boolean(publishState?.isPublished)
  const urls = { portal: links.portalUrl, direct: links.directUrl }

  const publish = async () => {
    setPublishing(true)
    try {
      await publishState.publishCourse()
      track('course_published')
      enqueueSnackbar('Published. Learners can open it now.', {
        variant: 'success',
      })
    } catch (error) {
      enqueueSnackbar('Could not publish this course.', {
        variant: 'error',
      })
    } finally {
      setPublishing(false)
    }
  }

  return (
    <div className={classes.view}>
      <div className={classes.head}>
        <div>
          <div className={classes.title}>Share this course</div>
          <div className={classes.subtitle}>
            Two links, same course. Pick based on where learners come from.
          </div>
        </div>
        <DomainPicker links={links} />
      </div>

      {LINK_CARDS.map(card => {
        const url = urls[card.key]
        const isCopied = copied === card.key
        return (
          <div key={card.key} className={classes.linkCard}>
            <div className={classes.linkTitleRow}>
              <span className={classes.linkTitle}>{card.title}</span>
              <span className={classes.badge} style={card.badgeStyle}>
                {card.badge}
              </span>
            </div>
            <div className={classes.linkRow}>
              <input readOnly value={url || ''} className={classes.linkInput} />
              <button
                type="button"
                className={`${classes.copyButton} ${
                  isCopied ? classes.copied : ''
                }`}
                disabled={!url}
                onClick={() => {
                  track('course_share_link_copied', { link_context: card.key })
                  copy(card.key, url)
                }}
              >
                <Icon
                  icon={isCopied ? 'tabler:check' : 'tabler:copy'}
                  width={13}
                  height={13}
                />
                {isCopied ? 'Copied' : 'Copy'}
              </button>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.openButton}
              >
                <Icon icon="tabler:external-link" width={13} height={13} />
                Open
              </a>
            </div>
            <div className={classes.hint}>{card.hint}</div>
          </div>
        )
      })}

      <div className={classes.settingRow}>
        <div>
          <div className={classes.settingTitle}>Show in portal catalog</div>
          <div className={classes.hint}>
            Turn off to keep this course reachable only by its links.
          </div>
        </div>
        <Toggle checked={listedInCatalog} onChange={setListedInCatalog} />
      </div>

      {publishState?.isLoaded && (
        <div className={classes.draftRow}>
          <button
            type="button"
            className={classes.draftButton}
            disabled={publishing}
            onClick={isPublished ? onMoveToDraft : publish}
          >
            <Icon
              icon={isPublished ? 'tabler:eye-off' : 'tabler:eye'}
              width={16}
              height={16}
            />
            {isPublished ? 'Move to draft' : 'Publish'}
          </button>
          <span className={classes.muted}>
            Access follows the course's authentication.
          </span>
        </div>
      )}

      {publishState?.isLoaded && !isPublished && (
        <div className={classes.warning}>
          <span className={classes.warningIcon}>
            <Icon icon="tabler:alert-triangle" width={14} height={14} />
          </span>
          This course is in draft. Neither link works for learners until you
          publish it.
        </div>
      )}
    </div>
  )
}

export default LinksView
